import React from "react";
import { Card, CardContent } from "../components/ui/card";

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-purple-950/5 to-background py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
              Terms of Service
            </span>
          </h1>
          <p className="text-foreground/60">Last Updated: January 2025</p>
        </div>

        <Card className="bg-card/50 backdrop-blur-sm border-border/40">
          <CardContent className="p-8 space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">1. Agreement to Terms</h2>
              <p className="text-foreground/70 leading-relaxed">
                By accessing or using Cogsmith's desktop application and website (collectively, the "Services"), 
                you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these 
                Terms, you may not access or use our Services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">2. License and Use</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-foreground/90">2.1 License Grant</h3>
              <p className="text-foreground/70 leading-relaxed mb-4">
                Subject to your compliance with these Terms, we grant you a limited, non-exclusive, 
                non-transferable, revocable license to use the Cogsmith desktop application for your 
                personal or internal business purposes.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-foreground/90">2.2 Buy Once, Keep Forever</h3>
              <p className="text-foreground/70 leading-relaxed mb-4">
                When you purchase Cogsmith, you receive a perpetual license to use the version you purchased. 
                This license does not expire, and you may continue to use that version indefinitely. However, 
                access to updates, new features, and support may be subject to additional terms or fees.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-foreground/90">2.3 Restrictions</h3>
              <p className="text-foreground/70 leading-relaxed mb-2">
                You agree not to:
              </p>
              <ul className="list-disc list-inside text-foreground/70 space-y-2 ml-4">
                <li>Modify, reverse engineer, decompile, or disassemble the software</li>
                <li>Remove or alter any proprietary notices or labels</li>
                <li>Use the Services for any illegal or unauthorized purpose</li>
                <li>Distribute, sublicense, or transfer your license to any third party</li>
                <li>Use the Services to harm, threaten, or harass others</li>
                <li>Attempt to gain unauthorized access to any portion of the Services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">3. User Accounts</h2>
              <p className="text-foreground/70 leading-relaxed mb-4">
                To purchase and download Cogsmith, you may need to create an account. You are responsible for:
              </p>
              <ul className="list-disc list-inside text-foreground/70 space-y-2 ml-4">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use of your account</li>
                <li>Ensuring that your account information is accurate and up-to-date</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">4. Third-Party Services</h2>
              <p className="text-foreground/70 leading-relaxed mb-4">
                Cogsmith may integrate with third-party services (such as AI providers, cloud services, and 
                development tools). Your use of these third-party services is subject to their respective 
                terms of service and privacy policies. We are not responsible for the availability, accuracy, 
                or content of third-party services.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                You are solely responsible for obtaining and maintaining any necessary accounts, API keys, 
                and credentials required to use third-party services with Cogsmith.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">5. Intellectual Property</h2>
              <p className="text-foreground/70 leading-relaxed mb-4">
                The Services, including all content, features, and functionality, are owned by Cogsmith and 
                are protected by international copyright, trademark, patent, trade secret, and other 
                intellectual property laws.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                You retain all rights to the content you create using Cogsmith. We do not claim ownership 
                of your data, notes, code, or any other content generated through your use of the Services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">6. Payment and Refunds</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-foreground/90">6.1 Pricing</h3>
              <p className="text-foreground/70 leading-relaxed mb-4">
                All prices are listed in U.S. dollars and are subject to change. We reserve the right to 
                modify our pricing at any time, but price changes will not affect purchases already made.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-foreground/90">6.2 Payment Processing</h3>
              <p className="text-foreground/70 leading-relaxed mb-4">
                Payments are processed through secure third-party payment processors. We do not store your 
                complete payment card information on our servers.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-foreground/90">6.3 Refunds</h3>
              <p className="text-foreground/70 leading-relaxed">
                We offer a 30-day money-back guarantee. If you are not satisfied with Cogsmith, you may 
                request a full refund within 30 days of purchase by contacting us at info@cogsmith.ai.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">7. Disclaimer of Warranties</h2>
              <p className="text-foreground/70 leading-relaxed mb-4">
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
                EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, 
                FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                We do not warrant that the Services will be uninterrupted, error-free, or completely secure. 
                You use the Services at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">8. Limitation of Liability</h2>
              <p className="text-foreground/70 leading-relaxed mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, COGSMITH SHALL NOT BE LIABLE FOR ANY INDIRECT, 
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, 
                WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER 
                INTANGIBLE LOSSES.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                Our total liability to you for any claims arising from or related to the Services shall not 
                exceed the amount you paid us in the twelve (12) months preceding the claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">9. Indemnification</h2>
              <p className="text-foreground/70 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Cogsmith and its officers, directors, 
                employees, and agents from any claims, liabilities, damages, losses, and expenses, including 
                reasonable attorneys' fees, arising out of or in any way connected with your access to or 
                use of the Services or your violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">10. Termination</h2>
              <p className="text-foreground/70 leading-relaxed mb-4">
                We may terminate or suspend your access to the Services immediately, without prior notice or 
                liability, for any reason, including if you breach these Terms.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                Upon termination, your right to use the Services will immediately cease. However, your 
                perpetual license to use the version you purchased will remain valid unless terminated for 
                cause (such as violation of these Terms).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">11. Governing Law</h2>
              <p className="text-foreground/70 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United 
                States, without regard to its conflict of law provisions. Any legal action or proceeding 
                arising under these Terms will be brought exclusively in the federal or state courts located 
                in the United States.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">12. Changes to Terms</h2>
              <p className="text-foreground/70 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify you of any material 
                changes by posting the new Terms on our website and updating the "Last Updated" date. Your 
                continued use of the Services after such changes constitutes your acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">13. Severability</h2>
              <p className="text-foreground/70 leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision will 
                be limited or eliminated to the minimum extent necessary so that these Terms will otherwise 
                remain in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">14. Entire Agreement</h2>
              <p className="text-foreground/70 leading-relaxed">
                These Terms constitute the entire agreement between you and Cogsmith regarding the Services 
                and supersede all prior agreements and understandings, whether written or oral.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-foreground">15. Contact Information</h2>
              <p className="text-foreground/70 leading-relaxed mb-4">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="bg-purple-950/20 border border-purple-500/20 rounded-lg p-4">
                <p className="text-foreground/80">
                  <strong>Email:</strong>{" "}
                  <a href="mailto:info@cogsmith.ai" className="text-purple-400 hover:text-purple-300">
                    info@cogsmith.ai
                  </a>
                </p>
              </div>
            </section>

            <section className="bg-purple-950/10 border border-purple-500/20 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-3 text-foreground">Acceptance of Terms</h2>
              <p className="text-foreground/70 leading-relaxed">
                By downloading, installing, or using Cogsmith, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms of Service. If you do not agree to these Terms, do not 
                use our Services.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Terms;

